import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Star, ArrowRight } from 'lucide-react';
import { fetchSpotlightRecipes, Recipe } from '@/lib/recipes';
import { useAuth } from '@/contexts/AuthContext';
import LoadingSpinner from '@/components/LoadingSpinner';
import styles from './HomePage.module.css';

function RecipeCard({ recipe }: { recipe: Recipe }) {
  return (
    <Link to={`/recipes/${recipe.id}`} className={styles.card}>
      <div className={styles.imageWrapper}>
        <img
          src={recipe.image}
          alt={recipe.title}
          className={styles.image}
          loading="lazy"
          onError={(e) => {
            (e.target as HTMLImageElement).src = '/placeholder-recipe.png';
          }}
        />
      </div>
      <div className={styles.cardContent}>
        <h3 className={styles.cardTitle}>{recipe.title}</h3>
        <div className={styles.cardMeta}>
          {recipe.rating > 0 && (
            <span className={styles.rating}>
              <Star size={16} fill="currentColor" />
              {recipe.rating.toFixed(1)}
            </span>
          )}
          {recipe.calories > 0 && <span className={styles.calories}>{recipe.calories} cal</span>}
        </div>
      </div>
    </Link>
  );
}

export default function HomePage() {
  const { user, profile } = useAuth();

  const { data: recipes, isLoading } = useQuery({
    queryKey: ['spotlight-recipes'],
    queryFn: fetchSpotlightRecipes,
  });

  const greeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <h1 className={styles.greeting}>
          {greeting()}{profile?.first_name ? `, ${profile.first_name}` : ''}!
        </h1>
        <p className={styles.subtitle}>
          Discover healthy recipes tailored to your wellness journey
        </p>
      </header>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Top Rated Recipes</h2>
          <Link to="/recipes" className={styles.viewAll}>
            View all <ArrowRight size={18} />
          </Link>
        </div>

        <div className={styles.grid}>
          {recipes?.map((recipe) => (
            <RecipeCard key={recipe.id} recipe={recipe} />
          ))}
        </div>
      </section>

      <section className={styles.quickActions}>
        <Link to="/cookbooks" className={styles.actionCard}>
          <h3>Browse Cookbooks</h3>
          <p>Explore our curated health guides</p>
        </Link>
        <Link to="/meal-plan" className={styles.actionCard}>
          <h3>Meal Planning</h3>
          <p>Plan your weekly meals</p>
        </Link>
        <Link to="/calories" className={styles.actionCard}>
          <h3>Track Calories</h3>
          <p>Monitor your daily intake</p>
        </Link>
      </section>
    </div>
  );
}
